// composer 语音消息录制——纯函数(时长格式化/编码格式选择/URL 类型判定) +
// 一个薄的 MediaRecorder 生命周期封装类。
//
// 编排类(VoiceRecorderSession)本身不写 node --test —— 摸 getUserMedia/
// MediaRecorder/Blob，是 native-bridge.js / permission-bridge.js 已经建立的
// "浏览器 API 胶水层不单测，只测它周围的纯决策函数"惯例的延伸。
//
// 权限预检不是这个模块的职责：调用方(chat.js)在 new VoiceRecorderSession()
// 之前先跑 permission-bridge.js 的 ensureMediaPermission('microphone')，
// blocked 就不会走到这里。start() 里的 getUserMedia 本身，在纯 web 场景下
// 就是真正弹出浏览器原生权限框的地方——ensureMediaPermission 的 'proceed'
// 只代表"没被 bridge 挡住"，不代表"已经有权限"，这两层要分清楚。

export const MAX_RECORDING_SECONDS = 60;

// 优先级列表：MediaRecorder.isTypeSupported() 依次探测，取第一个支持的。
// Chrome/Firefox/Android WebView 桌面向支持 audio/webm;codecs=opus；
// Safari/iOS 不支持 webm 容器，回退到 audio/mp4（AAC）。
export const RECORDER_MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
];

/**
 * @param {string[]} candidates
 * @param {(mimeType: string) => boolean} isSupported 通常传
 *   `(t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)`
 * @returns {string|null} 找不到支持的就返回 null（调用方应回退到不传 mimeType
 *   直接 `new MediaRecorder(stream)`，让浏览器自己选默认格式）
 */
export function pickRecorderMimeType(candidates, isSupported) {
    for (const c of candidates) {
        try {
            if (isSupported(c)) return c;
        } catch (e) {
            // 个别实现对陌生的 mimeType 字符串会抛而不是返回 false，当不支持处理
        }
    }
    return null;
}

export function formatVoiceDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
}

// 到点自动停止（转成预览态，不是自动发送——访客还能选择丢弃或发送）。
export function shouldAutoStop(elapsedSeconds, maxSeconds = MAX_RECORDING_SECONDS) {
    return elapsedSeconds >= maxSeconds;
}

// mimeType → 上传时给 File 对象取的扩展名（只是给文件起个像样的名字，服务端
// 落盘扩展名不采信这个，是自己用 finfo 探测真实 MIME 反查固定表决定的）。
export function extensionHintForMimeType(mimeType) {
    const m = String(mimeType || '').toLowerCase();
    if (m.includes('mp4')) return 'mp4';
    if (m.includes('ogg')) return 'ogg';
    return 'webm';
}

// 消息渲染层用：这条 attachment URL 是不是一条语音录制。跟服务端
// MessageTypeResolver::inferFromUrl() 的后缀表保持一致（.weba 是服务端给
// webm/mp4 语音录制统一落盘用的扩展名，见 ApiController::upload() 的
// kind='voice' 分支注释）。
export function isVoiceAttachmentUrl(url) {
    return typeof url === 'string' && /\.(weba|ogg|opus|m4a)(\?|$)/i.test(url);
}

/**
 * getUserMedia + MediaRecorder 的生命周期封装。用法：
 *   const session = new VoiceRecorderSession({ onTick, onStop, onError });
 *   const started = await session.start();  // false = 没能开始录（listen onError 拿原因）
 *   session.stop();    // 正常结束 → onStop({ blob, mimeType, seconds })
 *   session.cancel();  // 中途放弃 → 不触发 onStop，直接释放流
 */
export class VoiceRecorderSession {
    constructor({ onTick, onStop, onError } = {}) {
        this.onTick = onTick || (() => {});
        this.onStop = onStop || (() => {});
        this.onError = onError || (() => {});
        this._stream = null;
        this._recorder = null;
        this._chunks = [];
        this._startedAt = 0;
        this._tickTimer = null;
        this._cancelled = false;
    }

    async start() {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.onError('unsupported');
            return false;
        }
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e) {
            // 纯 web 场景下，ensureMediaPermission 前置检查过不了 bridge 就已经
            // return 了；走到这里说明是浏览器原生权限框本身被拒绝/无设备。
            this.onError('denied');
            return false;
        }
        if (typeof MediaRecorder === 'undefined') {
            stream.getTracks().forEach((tr) => { try { tr.stop(); } catch (e2) {} });
            this.onError('unsupported');
            return false;
        }
        const mimeType = pickRecorderMimeType(
            RECORDER_MIME_CANDIDATES,
            (t) => MediaRecorder.isTypeSupported(t)
        );
        let recorder;
        try {
            recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        } catch (e) {
            stream.getTracks().forEach((tr) => { try { tr.stop(); } catch (e2) {} });
            this.onError('unsupported');
            return false;
        }

        this._stream = stream;
        this._recorder = recorder;
        this._chunks = [];
        this._cancelled = false;

        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) this._chunks.push(e.data);
        };
        recorder.onstop = () => {
            this._clearTick();
            const usedMimeType = recorder.mimeType || mimeType || 'audio/webm';
            const seconds = Math.round((Date.now() - this._startedAt) / 1000);
            const blob = new Blob(this._chunks, { type: usedMimeType });
            this._releaseStream();
            if (!this._cancelled) {
                this.onStop({ blob, mimeType: usedMimeType, seconds });
            }
        };

        this._startedAt = Date.now();
        recorder.start();
        this._tickTimer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - this._startedAt) / 1000);
            this.onTick(elapsed);
            if (shouldAutoStop(elapsed)) {
                this.stop();
            }
        }, 250);
        return true;
    }

    stop() {
        this._clearTick();
        if (this._recorder && this._recorder.state !== 'inactive') {
            try { this._recorder.stop(); } catch (e) {}
        }
    }

    cancel() {
        this._cancelled = true;
        this._clearTick();
        if (this._recorder && this._recorder.state !== 'inactive') {
            try { this._recorder.stop(); } catch (e) {}
        } else {
            this._releaseStream();
        }
    }

    _clearTick() {
        if (this._tickTimer) {
            clearInterval(this._tickTimer);
            this._tickTimer = null;
        }
    }

    _releaseStream() {
        if (this._stream) {
            this._stream.getTracks().forEach((tr) => { try { tr.stop(); } catch (e) {} });
            this._stream = null;
        }
    }
}
